import React, { useState } from 'react';
import { Star, Loader2, X } from 'lucide-react';
import { providerReviewsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DIMENSIONS = [
  { key: 'quality', label: 'Quality of Work' },
  { key: 'reliability', label: 'Reliability & Punctuality' },
  { key: 'value', label: 'Value for Money' },
  { key: 'communication', label: 'Communication' },
];

const StarRating = ({ value, onChange, size = 24 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            size={size}
            className={`transition-colors ${
              s <= (hover || value) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ProviderReviewForm = ({ providerId, jobId, jobType = 'service_request', onSubmitted, onCancel }) => {
  const { getAuthToken } = useAuth();
  const [ratings, setRatings] = useState({ quality: 0, reliability: 0, value: 0, communication: 0 });
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const overallRating = Object.values(ratings).filter(v => v > 0).length === 4
    ? (Object.values(ratings).reduce((a, b) => a + b, 0) / 4).toFixed(1)
    : null;

  const allRated = Object.values(ratings).every(v => v > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRated) {
      setError('Please rate all categories');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = getAuthToken();
      await providerReviewsAPI.create({
        providerId,
        jobId,
        jobType,
        ratings,
        comment: comment.trim(),
      }, token);
      onSubmitted?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">Write a Review</h3>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Rating Dimensions */}
      <div className="space-y-4 mb-6">
        {DIMENSIONS.map(dim => (
          <div key={dim.key} className="flex items-center justify-between">
            <span className="text-sm text-gray-700 font-medium">{dim.label}</span>
            <StarRating
              value={ratings[dim.key]}
              onChange={(val) => setRatings(prev => ({ ...prev, [dim.key]: val }))}
              size={22}
            />
          </div>
        ))}
      </div>

      {/* Overall */}
      {overallRating && (
        <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-amber-50 rounded-lg">
          <Star size={20} className="text-amber-400 fill-amber-400" />
          <span className="text-lg font-bold text-amber-700">{overallRating}</span>
          <span className="text-sm text-amber-600">Overall Rating</span>
        </div>
      )}

      {/* Comment */}
      <div className="mb-4">
        <label className="text-sm font-medium text-gray-600 block mb-1">Your Review</label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Tell others about your experience with this provider..."
          rows={3}
          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68] resize-none"
        />
      </div>

      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

      <div className="flex gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 border text-gray-600 rounded-lg hover:bg-gray-50 text-sm">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !allRated}
          className="flex-1 px-4 py-2 bg-[#1F3A68] text-white rounded-lg hover:bg-[#162d52] disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : 'Submit Review'}
        </button>
      </div>
    </form>
  );
};

export default ProviderReviewForm;
